import { useCallback, useEffect, useRef } from "react";

export const useScrollPositionPersistence = <T extends HTMLElement = HTMLDivElement>(storageKey: string, isDataReady?: boolean) => {
  const scrollContainerRef = useRef<T>(null);
  const hasRestoredRef = useRef(false);
  const pendingRestoreRef = useRef<number | null>(null);

  useEffect(() => {
    if (hasRestoredRef.current || !isDataReady) return;

    try {
      const savedPosition = sessionStorage.getItem(storageKey);

      if (savedPosition && scrollContainerRef.current) {
        const position = parseInt(savedPosition, 10);

        const timeoutId = setTimeout(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: position, behavior: "auto" });
            hasRestoredRef.current = true;
          }
        }, 0);

        return () => clearTimeout(timeoutId);
      }
      hasRestoredRef.current = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Failed to restore scroll position:", error);
      hasRestoredRef.current = true;
    }
  }, [isDataReady, storageKey]);

  useEffect(() => {
    hasRestoredRef.current = false;
    pendingRestoreRef.current = null;
  }, [storageKey]);

  const saveScrollPosition = useCallback(() => {
    if (scrollContainerRef.current) {
      try {
        const position = scrollContainerRef.current.scrollTop;
        sessionStorage.setItem(storageKey, position.toString());
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Failed to save scroll position:", error);
      }
    }
  }, [storageKey]);

  return { scrollContainerRef, saveScrollPosition };
};
